import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, CircularProgress } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { Alert } from '../../../../../../shared/lib/common/Alert';
import { CustomController } from '../../../../../../shared/lib/common/CustomController';
import { authBackdropStyle, authCardStyle, authCardWrapper } from '../../../../../../shared/lib/theme/styleConstants';
import { AuthContainer } from '../../../../components/auth/AuthContainer';
import Verify from '../../../../components/auth/Verify';
import { PageHeading } from '../../../../../../shared/lib/common/PageHeading';
import {
  ALERT_EMAIL_SENT_SUCCESS_SHORT,
  BUTTON_TEXT_BACK_TO_LOGIN,
  BUTTON_TEXT_RESET_PASSWORD,
  LABEL_EMAIL,
  PAGE_SUBTITLE_FORGOT_PASSWORD,
  PAGE_TITLE_FORGOT_PASSWORD
} from '../../../../../../shared/lib/constants';
import { ForgotPasswordData } from '../../../interfaceTypes';
import { forgotPasswordValidationSchema } from '../../../validationSchema';
import { LINKS, THERE_WAS_SOME_ERROR_WHILE_SENDING_EMAIL, USER_DOES_NOT_EXIST } from '../../../constants';
import palette from '../../../../../../shared/lib/theme/palette';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LoginType, useForgotPasswordMutation } from '../../../../generated';
import { useMobileView } from '../../../hooks/useMobileView';

const ForgotPassword = () => {
  const [email, setEmail] = useState<string>('');
  const [verify, setVerify] = useState<boolean>(false);
  const { blackOne } = palette;
  const navigate = useNavigate();

  const methods = useForm<ForgotPasswordData, any, ForgotPasswordData>({
    mode: 'all',
    resolver: yupResolver(forgotPasswordValidationSchema),

    defaultValues: {
      email: ''
    }
  });

  const {
    handleSubmit,
    formState: { isValid, isSubmitting }
  } = methods;

  const [forgotPassword, { loading }] = useForgotPasswordMutation({
    onError({ message }) {
      if (message === USER_DOES_NOT_EXIST) Alert.error(USER_DOES_NOT_EXIST);
      else if (message === THERE_WAS_SOME_ERROR_WHILE_SENDING_EMAIL) Alert.error(THERE_WAS_SOME_ERROR_WHILE_SENDING_EMAIL);
      else Alert.error(message);
    },

    onCompleted() {
      Alert.success(ALERT_EMAIL_SENT_SUCCESS_SHORT);
      setVerify(true);
    }
  });

  const onSubmit: SubmitHandler<ForgotPasswordData> = async (data) => {
    const { email } = data;
    setEmail(email);

    await forgotPassword({
      variables: {
        forgotPasswordInput: {
          email: email,
          loginType: LoginType.Portal
        }
      }
    });
  };

  const handleBackToLogin = () => {
    navigate(LINKS.LOGIN_LINK);
  };

  const mediumViewport = useMobileView();

  return (
    <AuthContainer>
      <Box sx={authBackdropStyle} />

      <Box flex={mediumViewport ? 'none' : 1} sx={authCardWrapper}>
        {!verify ? (
          <Box sx={authCardStyle} textAlign="center">
            <PageHeading color={blackOne} title={PAGE_TITLE_FORGOT_PASSWORD} subTitle={PAGE_SUBTITLE_FORGOT_PASSWORD} />

            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <CustomController controllerName="email" controllerLabel={LABEL_EMAIL} fieldType="email" />

                <Button variant="contained" sx={{ mt: 2 }} type="submit" disabled={loading || !isValid || isSubmitting} endIcon={loading && <CircularProgress size={20} color="inherit" />} fullWidth>
                  {BUTTON_TEXT_RESET_PASSWORD}
                </Button>

                <Box textAlign="center">
                  <Button onClick={handleBackToLogin} variant="text" sx={{ color: 'primary', mt: '24px' }} startIcon={<ArrowBackIcon sx={{ color: 'primary' }} />}>
                    {BUTTON_TEXT_BACK_TO_LOGIN}
                  </Button>
                </Box>
              </form>
            </FormProvider>
          </Box>
        ) : (
          <Box sx={authCardStyle}>
            <Verify email={email} />
          </Box>
        )}
      </Box>
    </AuthContainer>
  );
};

export default ForgotPassword;
